// border — optional Polaroid frame. Allocates a NEW canvas at the framed
// dimensions, copies the existing bitmap into the interior, fills the frame
// with paper white.
//
// The -1° share-rotation lives in the renderer that consumes this, not here —
// border returns a flat framed bitmap and lets the share/recap layer apply
// transform: rotate(-1deg) at render time.
//
// Because this filter resizes the canvas, it returns a new FilterContext (with
// updated width/height + ctx pointed at the new canvas). Other filters operate
// in-place, so this is the one exception. Callers swap the context before
// proceeding with subsequent passes.

import type { FilterContext } from './_helpers'

export type BorderOpts = {
  mode: 'none' | 'polaroid'
}

export const POLAROID_TOP = 24
export const POLAROID_SIDE = 24
export const POLAROID_BOTTOM = 80
export const POLAROID_FILL = '#fffaf0' // --paper

export type BorderResult = {
  canvas: CanvasImageSource & { width: number; height: number }
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D
  width: number
  height: number
}

// Returns either the same canvas/ctx (when mode === 'none') or a new framed
// canvas/ctx (when mode === 'polaroid'). Callers should rebuild their
// FilterContext from the return value.
export function border(c: FilterContext, opts: BorderOpts): BorderResult {
  if (opts.mode === 'none') {
    return {
      canvas: c.ctx.canvas as CanvasImageSource & { width: number; height: number },
      ctx: c.ctx,
      width: c.width,
      height: c.height,
    }
  }
  return drawPolaroidFrame(c)
}

function drawPolaroidFrame(c: FilterContext): BorderResult {
  const outW = c.width + POLAROID_SIDE * 2
  const outH = c.height + POLAROID_TOP + POLAROID_BOTTOM

  // Build a sibling canvas of the same flavor (OffscreenCanvas if the source
  // was one). We can't import makeCanvas here without a circular dep — call
  // the constructors directly.
  let outCanvas: OffscreenCanvas | HTMLCanvasElement
  if (
    typeof OffscreenCanvas !== 'undefined' &&
    c.ctx.canvas instanceof OffscreenCanvas &&
    typeof OffscreenCanvas.prototype.transferToImageBitmap === 'function'
  ) {
    outCanvas = new OffscreenCanvas(outW, outH)
  } else {
    outCanvas = document.createElement('canvas')
    outCanvas.width = outW
    outCanvas.height = outH
  }

  const outCtx = outCanvas.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null
  if (!outCtx) {
    throw new Error('border: 2d context unavailable on framed canvas')
  }

  // Paint the frame paper-white, then copy the source onto the interior.
  outCtx.fillStyle = POLAROID_FILL
  outCtx.fillRect(0, 0, outW, outH)
  outCtx.drawImage(c.ctx.canvas as CanvasImageSource, POLAROID_SIDE, POLAROID_TOP)

  return {
    canvas: outCanvas as CanvasImageSource & { width: number; height: number },
    ctx: outCtx,
    width: outW,
    height: outH,
  }
}
